/** @jsx jsx */
import { jsx } from '@compiled/react';
import { ExperienceName, ScreenName } from '@helpcenter/constants';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import { withAsync } from '@helpcenter/with-async';
import { withPathParamProvider } from '@helpcenter/with-path-params';
import { lazyForPaint } from 'react-loosely-lazy';
import type { Route, RouteContext } from 'react-resource-router';
import type { State } from 'state';
import {
	NotFoundPage,
	RenderCreateAnonymousSuccess,
	RenderRequestCreateForHubs,
} from 'view/app/routes';
import { CsmArticle } from 'view/article/csm-variants';
import { CompleteSignup } from 'view/complete-signup';
import { InvitedSignUp } from 'view/invited-sign-up';
import { Login } from 'view/login';
import { PortalAccessForbidden } from 'view/portal-access-forbidden';
import { ResetPassword } from 'view/reset-password';
import { SignUp } from 'view/sign-up';
import { CheckEmail } from 'view/sign-up-check-email';
import { withHubPremiumSlaExperience } from 'view/hub-premium-sla';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { HubAppProps } from '@atlassian/company-hub-common';
import type { MigrationReview } from '@atlassian/company-hub-common/types';
import { HubPageType } from '@atlassian/company-hub-common/utils/hub-page-utils';
import { CsmContactUsForHubs } from '@atlassian/csm-contact-us';
import { CsmFolderForHubs } from '@atlassian/csm-folder';
import { CsmEmptyFolderResource } from '@atlassian/csm-folder/src/services/csm-empty-folder-resource';
import { CsmFolderResource } from '@atlassian/csm-folder/src/services/csm-folder-resource';
import { csmRequestDetailsQueryResource } from '@atlassian/csm-request-details/src/services/cms-request-details-resource';
import { mediaReadCredentialsResource } from '@atlassian/csm-request-details/src/services/media-read-credentials-resource';
import { mediaUploadCredentialsResource } from '@atlassian/csm-request-details/src/services/media-upload-credentials-resource';
import { requestConversationResource } from '@atlassian/csm-request-details/src/services/request-conversation-resource';
import { AsyncCsmRequestDetails } from '@atlassian/csm-request-details/src/ui/index';
import { CsmRequestsListForHubs } from '@atlassian/csm-requests-list';
import { AsyncSurveyDetails } from '@atlassian/surveys-portal';
import { csmRequestsListQueryResource } from '@atlassian/csm-requests-list/src/services/csm-requests-list-resource';
import { mediaUploadCredentialsResource as mediaUploadCredentialsResourceForContactUs } from '@atlassian/customer-service/src/services/media-credentials-upload-resource';
import {
	getIsHelpHub,
	isCSMHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import {
	getHoistedProjectKey,
	getIsHCCustomizationEnabled,
	getJiraDisplayUrl,
	getEnv,
} from '@atlassian/help-center-common-util/env';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { assign } from '@atlassian/help-center-common-util/location';
import { initialModel } from '@atlassian/help-center-common-util/model';
import { withSetThemeColorInRedux } from '@atlassian/help-center-common-util/with-set-theme-color-in-redux';
import { customizingRequestListColumn } from '@helpcenter/requests-column-settings';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getCanAdministerJIRA } from 'state/selectors/user';
import { Search } from '@atlassian/help-center-search';
import { HubCustomizeMenu } from './hub-customize-menu';
import type { HubCustomizeMenuItem, HubCustomizeMenuSection } from './hub-customize-menu';
import { HubHomepageCustomizeNavActions } from './hub-customize-actions';
import { HubCustomizeNavActions } from './hub-customize-nav-actions';
import { useHelpCenterCustomizeSection } from './use-help-center-customize-section';
import hubMessages from './messages';
import {
	createHubHomepageRoute,
	createRouteWithHubShell as createRouteWithHubShellWithoutWrapper,
	createHubCustomPageRoute,
	createHubPortalPageRoute,
} from '@atlassian/hub';
import { logOutAction } from '../../epics/logout';
import { store } from '../../providers';
import {
	buildMigratedHelpCenterEditorUrl,
	isMigratedHelpCenterReviewEnabled,
	isMigratedHelpCenterReviewPath,
	leaveMigratedHelpCenterReview,
} from '../../utils/migrated-help-center-review';
import { parseHelpCenterAri } from '../../utils/ari-parser';
import { ApprovalList } from '../approval-list';
import { Article } from '../article';
import { HubAnonymousLayout } from '../hub-anonymous-layout';
import { HubAvatarMenuOptions } from '../hub-avatar-menu-options';
import { RequestApproval } from '../request-approval';
import { RequestDetails } from '../request-details';
import { RequestFeedback } from '../request-feedback';
import { RequestList } from '../request-list';
import { Unsubscribe } from '../unsubscribe';
import { CsmUserProfileForHubs } from '../user-profile/csm-hub-variants';
import { UserProfileForHubs } from '../user-profile/hub-variant';
import { HUB_ROUTES, JSM_HUB_CUSTOMIZE_MENU_GATE } from './constants';
import { HubExtensionsDependenciesProvider } from '../hub-extensions-bridge';
import { withStaticHubBreadcrumbs } from './static-hub-breadcrumbs';

// Dynamic import, not a static one: `routes/kb` statically pulls in
// `view/knowledge-category`, whose graph reaches `packages/util/url` and calls
// `isCSMHelpCenter(getEnv())` at module scope. Loading it lazily keeps that out of
// this module's chunk and out of test-time module evaluation.
const AsyncArticlesSearch = withAsync({
	loader: lazyForPaint(() =>
		// Relative, not the `routes/kb` alias: the lazy-import babel transform resolves
		// this specifier itself and does not apply webpack's `resolve.modules`.
		import(/* webpackChunkName: "hub-all-search-results" */ '../../routes/kb').then((m) => ({
			default: m.RenderArticlesSearchRoute,
		})),
	),
});
const editMigratedHelpCenterDraft = (): void => {
	const { helpCenterId } = parseHelpCenterAri(getHelpCenterAri() ?? '');
	assign(buildMigratedHelpCenterEditorUrl({ helpCenterId }));
};

const MIGRATED_HELP_CENTER_REVIEW: MigrationReview = {
	onEditDraft: editMigratedHelpCenterDraft,
	onFinishLater: leaveMigratedHelpCenterReview,
};

const renderAvatarMenuOptions = () => {
	return <HubAvatarMenuOptions />;
};

export const isJsmHubCustomizeMenuEnabled = (): boolean =>
	FeatureGates.checkGate(JSM_HUB_CUSTOMIZE_MENU_GATE);

const createHubRouteProps = (shouldRenderRightNavActions: boolean): HubAppProps => {
	const initialModelState = initialModel();
	const helpCenterAri = getHelpCenterAri();

	const renderRightNavActions = () => {
		return <Search trigger="icon" />;
	};

	const isHelpHub = getIsHelpHub();
	const isCSMHelpCenterFlag = isCSMHelpCenter(getEnv().helpCenterType);
	const { helpCenterId } = parseHelpCenterAri(helpCenterAri ?? '');
	const isMigratedHelpCenterReviewPathActive = isMigratedHelpCenterReviewPath(
		globalThis.location.pathname,
	);
	const migrationReview: MigrationReview | undefined =
		isMigratedHelpCenterReviewEnabled() &&
		isHelpHub &&
		isMigratedHelpCenterReviewPathActive &&
		helpCenterAri?.endsWith('-hubs')
			? MIGRATED_HELP_CENTER_REVIEW
			: undefined;

	return {
		renderRightNavActions: shouldRenderRightNavActions ? renderRightNavActions : undefined,
		...(FeatureGates.checkGate('forge-on-hubs')
			? { productExtensionsDependenciesProvider: HubExtensionsDependenciesProvider }
			: {}),
		renderAvatarMenuOptions: isHelpHub ? renderAvatarMenuOptions : undefined,
		// Whether the site may customise its help center — the same attribute the
		// avatar menu reads for its Premium pill, and what the editor passes in
		// `HubEditLayout`. The view experience needs it too, because the Customise
		// menu renders here: without it `useShouldShowPremiumUpsell` only ever sees
		// `undefined` and no upsell could show. Tri-state, so `undefined` never gates,
		// and CSM is excluded by the hook's own `hubType` check rather than here.
		isPremiumEntitled: getIsHCCustomizationEnabled(),
		...(migrationReview ? { migrationReview } : {}),
		user: {
			email: initialModelState.user?.email || '',
			displayName: initialModelState.user?.displayName || '',
			avatarSrc: initialModelState.user?.avatars?.xlarge || '',
			isJiraAdmin: initialModelState.user?.canAdministerJIRA || false,
			id: '',
		},
		env: {
			helpCenterId,
			helpCenterAri,
			cloudId: getCloudId(),
			jiraDisplayUrl: getJiraDisplayUrl(),
			hoistedProjectKey: getHoistedProjectKey() || '',
			relativeBaseUrl: getRelativeBasePath(),
		},
		featureFlags: {
			enable_hub_breadcrumbs: isHelpHub,
			enable_rovo_in_help_hubs: isHelpHub && FeatureGates.checkGate('enable_rovo_in_help_hubs'),
			hub_component_upgrade: isHelpHub && FeatureGates.checkGate('hub_component_upgrade'),
			csm_callout_banner_component_release:
				isCSMHelpCenterFlag && FeatureGates.checkGate('csm_callout_banner_component_release'),
			hub_editor_floating_toolbar:
				isHelpHub && FeatureGates.checkGate('hub_editor_floating_toolbar'),
			'portal-pages-in-hub': isHelpHub && FeatureGates.checkGate('portal-pages-in-hub'),
			// Product-level flag that opts a hub surface into advanced theming
			// v2. Today only JSM Help Hubs are opted in; the platform-level
			// `hub-advanced-theming` Statsig gate AND-combines with this so
			// rollout is independently controlled from both sides.
			'advanced-theming-in-hub': isHelpHub && FeatureGates.checkGate('advanced-theming-in-jsm'),
			translation_support: isHelpHub,
			help_hubs_configurable_site_tree: isHelpHub,
		},
		actions: {
			onLogoutClick: () => {
				const action = logOutAction();
				store.dispatch(action);
				return action;
			},
		},
	};
};

const createRouteWithHubShell = (
	...args: Parameters<typeof createRouteWithHubShellWithoutWrapper>
) => {
	const [props, component, ...restArgs] = args;
	return createRouteWithHubShellWithoutWrapper(
		props,
		withSetThemeColorInRedux(withPathParamProvider(component)),
		...restArgs,
	);
};

/**
 * Route props carrying the Customise menu. `pageType` is only for pages that can
 * be edited from it; everything else gets the shared help center section alone.
 *
 * Guarded on `getIsHelpHub()` because several of these routes are registered by the
 * CSM router as well as the hub one — home, custom page, not found and portal
 * access forbidden are all shared — so being wired here is not enough to mean the
 * caller is a help hub. Same guard `createHubRouteProps` puts on
 * `renderAvatarMenuOptions`, and read inside the callback so it resolves at render
 * rather than at route registration.
 */
const createHubRoutePropsWithCustomizeMenu = (
	shouldRenderRightNavActions: boolean,
	pageType?: HubPageType,
): HubAppProps => ({
	...createHubRouteProps(shouldRenderRightNavActions),
	renderCustomNavActions: (iconColor?: string) =>
		getIsHelpHub() && isJsmHubCustomizeMenuEnabled() ? (
			<HubCustomizeNavActions iconColor={iconColor} pageType={pageType} />
		) : null,
});

export const getHubHomepageRoute = (): Route =>
	withHubPremiumSlaExperience(
		createHubHomepageRoute({
			...createHubRouteProps(false),
			renderCustomNavActions: (iconColor?: string) =>
				getIsHelpHub() && isJsmHubCustomizeMenuEnabled() ? (
					<HubHomepageCustomizeNavActions iconColor={iconColor} />
				) : null,
		}),
		ExperienceName.HELP_CENTER,
		ScreenName.HELP_CENTER,
	);

export const getHubCustompageRoute = (): Route =>
	createHubCustomPageRoute(createHubRoutePropsWithCustomizeMenu(true, HubPageType.CUSTOM));

/**
 * The hub portal page is the entry point into the Create Request journey, so a
 * failing JSM help object there counts against `request-create` rather than
 * `view-help-center`.
 */
export const getHubPortalPageRoute = (): Route =>
	withHubPremiumSlaExperience(
		createHubPortalPageRoute(createHubRoutePropsWithCustomizeMenu(true, HubPageType.PORTAL)),
		ExperienceName.REQUEST_CREATE,
		// The older help center's portal home, which mounts the same experience.
		ScreenName.PORTAL_HOME,
	);

export const getCsmUserProfileRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmUserProfileForHubs,
		HUB_ROUTES.USER_PROFILE.path,
		HUB_ROUTES.USER_PROFILE.name,
		[],
		'full-width',
	);

export const getCsmRequestsListRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmRequestsListForHubs,
		HUB_ROUTES.CSM_REQUESTS_LIST.path,
		HUB_ROUTES.CSM_REQUESTS_LIST.name,
		[csmRequestsListQueryResource],
		'full-width',
	);

export const getCsmContactUsRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmContactUsForHubs,
		HUB_ROUTES.CSM_CONTACT_US.path,
		HUB_ROUTES.CSM_CONTACT_US.name,
		[mediaUploadCredentialsResourceForContactUs],
		'full-width',
	);

export const getNotFoundRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: false },
		},
		NotFoundPage,
		HUB_ROUTES.NOT_FOUND.path,
		HUB_ROUTES.NOT_FOUND.name,
		[],
		'normal',
	);

export const getCsmFolderRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmFolderForHubs,
		HUB_ROUTES.CSM_FOLDER.path,
		HUB_ROUTES.CSM_FOLDER.name,
		[CsmFolderResource, CsmEmptyFolderResource],
		'full-width',
	);

export const getCsmRequestDetailsRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		AsyncCsmRequestDetails,
		HUB_ROUTES.CSM_REQUEST_DETAILS.path,
		HUB_ROUTES.CSM_REQUEST_DETAILS.name,
		[
			csmRequestDetailsQueryResource,
			requestConversationResource,
			mediaReadCredentialsResource,
			mediaUploadCredentialsResource,
		],
		'xxwide',
	);

export const getCsmViewPortalArticleRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmArticle,
		HUB_ROUTES.VIEW_PORTAL_ARTICLE.path,
		HUB_ROUTES.VIEW_PORTAL_ARTICLE.name,
		[],
		'wide',
	);

export const getHubViewPortalArticleRoute = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		Article,
		HUB_ROUTES.VIEW_PORTAL_ARTICLE.path,
		HUB_ROUTES.VIEW_PORTAL_ARTICLE.name,
		[],
		'wide',
	);

export const getPortalAccessForbiddenRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		PortalAccessForbidden,
		HUB_ROUTES.PORTAL_ACCESS_FORBIDDEN.path,
		HUB_ROUTES.PORTAL_ACCESS_FORBIDDEN.name,
		[],
		'normal',
	);

export const getCsmViewArticleRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmArticle,
		HUB_ROUTES.VIEW_ARTICLE.path,
		HUB_ROUTES.VIEW_ARTICLE.name,
		[],
		'full-width',
	);

export const getHubViewArticleRoute = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		Article,
		HUB_ROUTES.VIEW_ARTICLE.path,
		HUB_ROUTES.VIEW_ARTICLE.name,
		[],
		'normal',
	);

export const getHubViewKbRoute = (): Route =>
	createRouteWithHubShell(
		{ ...createHubRoutePropsWithCustomizeMenu(true), appearance: 'full-width' },
		Article,
		HUB_ROUTES.VIEW_KB_ARTICLE.path,
		HUB_ROUTES.VIEW_KB_ARTICLE.name,
		[],
		'normal',
	);

export const getCsmViewCrossSitePortalArticleRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmArticle,
		HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.path,
		HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.name,
		[],
		'normal',
	);
export const getHubViewCrossSitePortalArticleRoute = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		Article,
		HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.path,
		HUB_ROUTES.VIEW_CROSS_SITE_PORTAL_ARTICLE.name,
		[],
		'normal',
	);

export const getHubViewCrossSiteArticleRoute = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		Article,
		HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.path,
		HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.name,
		[],
		'normal',
	);

export const getCsmViewCrossSiteArticleRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRouteProps(true),
		CsmArticle,
		HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.path,
		HUB_ROUTES.VIEW_CROSS_SITE_ARTICLE.name,
		[],
		'normal',
	);

export const getLoginRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		() => (
			<HubAnonymousLayout>
				<Login />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.LOGIN.path,
		HUB_ROUTES.LOGIN.name,
		[],
		'note',
	);

export const getPortalLoginRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		() => (
			<HubAnonymousLayout>
				<Login />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.PORTAL_LOGIN.path,
		HUB_ROUTES.PORTAL_LOGIN.name,
		[],
		'note',
	);

export const getSignupRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<SignUp {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.SIGNUP.path,
		HUB_ROUTES.SIGNUP.name,
		[],
		'note',
	);

export const getPortalSignupRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<SignUp {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.PORTAL_SIGNUP.path,
		HUB_ROUTES.PORTAL_SIGNUP.name,
		[],
		'note',
	);

export const getCompleteSignupRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<CompleteSignup {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.COMPLETE_SIGNUP.path,
		HUB_ROUTES.COMPLETE_SIGNUP.name,
		[],
		'note',
	);

export const getPortalCompleteSignupRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<CompleteSignup {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.PORTAL_COMPLETE_SIGNUP.path,
		HUB_ROUTES.PORTAL_COMPLETE_SIGNUP.name,
		[],
		'note',
	);

export const getResetPasswordRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<ResetPassword {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.RESET_PASSWORD.path,
		HUB_ROUTES.RESET_PASSWORD.name,
		[],
		'note',
	);

export const getPortalResetPasswordRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<ResetPassword {...routeProps} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.PORTAL_RESET_PASSWORD.path,
		HUB_ROUTES.PORTAL_RESET_PASSWORD.name,
		[],
		'note',
	);

export const getCheckEmailRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		(routeProps: Omit<RouteContext, 'action'>) => (
			<HubAnonymousLayout>
				<CheckEmail {...routeProps} isLoading={false} />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.CHECK_EMAIL.path,
		HUB_ROUTES.CHECK_EMAIL.name,
		[],
		'note',
	);

export const getPortalInvitedSignupRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(false),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: true },
		},
		() => (
			<HubAnonymousLayout>
				<InvitedSignUp />
			</HubAnonymousLayout>
		),
		HUB_ROUTES.PORTAL_INVITED_SIGNUP.path,
		HUB_ROUTES.PORTAL_INVITED_SIGNUP.name,
		[],
		'note',
	);

export const getUnsubscribeRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: false },
		},
		Unsubscribe,
		HUB_ROUTES.UNSUBSCRIBE.path,
		HUB_ROUTES.UNSUBSCRIBE.name,
		[],
		'note',
	);

export const getRequestFeedbackRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: false },
		},
		RequestFeedback,
		HUB_ROUTES.REQUEST_FEEDBACK.path,
		HUB_ROUTES.REQUEST_FEEDBACK.name,
		[],
		'note',
	);

export const getAnonymousRequestSuccessRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: false },
		},
		RenderCreateAnonymousSuccess,
		HUB_ROUTES.ANONYMOUS_REQUEST_SUCCESS.path,
		HUB_ROUTES.ANONYMOUS_REQUEST_SUCCESS.name,
		[],
		'xxwide',
	);

/**
 * Covers the rest of the Create Request journey: the request form loading and the
 * submission succeeding. Both are already signalled by
 * `withExperienceNotifier('request-create-form')` inside the reused
 * `view/request-create-form` component — it just needs an `Experience` above it,
 * which the legacy request create page has and the hub route did not.
 */
export const getRequestCreateRouteWithHubShell = (): Route =>
	withHubPremiumSlaExperience(
		createRouteWithHubShell(
			createHubRoutePropsWithCustomizeMenu(true),
			RenderRequestCreateForHubs,
			HUB_ROUTES.REQUEST_CREATE.path,
			HUB_ROUTES.REQUEST_CREATE.name,
			[],
			'full-width',
		),
		ExperienceName.REQUEST_CREATE,
		ScreenName.REQUEST_CREATE,
	);

export const getHubUserProfileRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		withStaticHubBreadcrumbs(UserProfileForHubs, HUB_ROUTES.USER_PROFILE.name),
		HUB_ROUTES.USER_PROFILE.path,
		HUB_ROUTES.USER_PROFILE.name,
		[],
		'full-width',
	);

export const getJsmRequestDetailsRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		RequestDetails,
		HUB_ROUTES.JSM_REQUEST_DETAILS.path,
		HUB_ROUTES.JSM_REQUEST_DETAILS.name,
		[],
		'xxwide',
	);

const JsmCustomizeNavActions = ({ iconColor }: { iconColor?: string }) => {
	const state = store.getState() as State;
	const canAdministerJIRA = getCanAdministerJIRA(state);
	const { formatMessage } = useIntl();
	const handleManageListViewClick = useCallback(() => {
		store.dispatch(customizingRequestListColumn(true));
	}, []);
	const items = useMemo<HubCustomizeMenuItem[]>(() => {
		if (!canAdministerJIRA) {
			return [];
		}

		return [
			{
				label: formatMessage(hubMessages.manageListViewButtonLabel),
				testId: 'manage-list-view-button',
				onClick: handleManageListViewClick,
			},
		];
	}, [canAdministerJIRA, formatMessage, handleManageListViewClick]);

	const helpCenterSection = useHelpCenterCustomizeSection(canAdministerJIRA);
	const sections = useMemo(
		() =>
			[{ items }, helpCenterSection].filter((section): section is HubCustomizeMenuSection =>
				Boolean(section),
			),
		[items, helpCenterSection],
	);

	return <HubCustomizeMenu sections={sections} iconColor={iconColor} />;
};

const RequestListWithStaticHubBreadcrumbs = withStaticHubBreadcrumbs(
	RequestList,
	HUB_ROUTES.JSM_REQUESTS_LIST.name,
);

export const getJsmRequestsListRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRouteProps(true),
			layout: { showBanner: true, bannerText: 'Requests', isAnonymousRoute: false },
		},
		RequestListWithStaticHubBreadcrumbs,
		HUB_ROUTES.JSM_REQUESTS_LIST.path,
		HUB_ROUTES.JSM_REQUESTS_LIST.name,
		[],
		'xxwide',
	);

export const getJsmManageColumnsRequestsListRouteWithHubShell = (): Route => {
	const renderCustomNavActions = (iconColor?: string) => (
		<JsmCustomizeNavActions iconColor={iconColor} />
	);

	return createRouteWithHubShell(
		/**
		 * TODO: The banner text has to be internationalized
		 */
		{
			...createHubRouteProps(true),
			renderCustomNavActions,
			layout: { showBanner: true, bannerText: 'Requests', isAnonymousRoute: false },
		},
		RequestListWithStaticHubBreadcrumbs,
		HUB_ROUTES.JSM_REQUESTS_LIST.path,
		HUB_ROUTES.JSM_REQUESTS_LIST.name,
		[],
		'xxwide',
	);
};

export const getJsmApprovalsRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: 'Approvals', isAnonymousRoute: false },
		},
		withStaticHubBreadcrumbs(ApprovalList, HUB_ROUTES.JSM_APPROVALS.name),
		HUB_ROUTES.JSM_APPROVALS.path,
		HUB_ROUTES.JSM_APPROVALS.name,
		[],
		'xxwide',
	);

export const getJsmRequestApprovalRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		{
			...createHubRoutePropsWithCustomizeMenu(true),
			layout: { showBanner: true, bannerText: '', isAnonymousRoute: false },
		},
		RequestApproval,
		HUB_ROUTES.JSM_REQUEST_APPROVAL.path,
		HUB_ROUTES.JSM_REQUEST_APPROVAL.name,
		[],
		'xxwide',
	);

export const getSurveyRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		AsyncSurveyDetails,
		HUB_ROUTES.SURVEY.path,
		HUB_ROUTES.SURVEY.name,
		[],
		'xxwide',
	);

/**
 * The screen's search bar is `position: fixed` with no `left`, so it anchors to
 * its static position: `hideNavbar` keeps the navbar from landing under it, and
 * `full-width` avoids the shell's centred `max-width` wrapper, which would inset
 * the bar and push its `100vw` off-screen.
 */
export const getHubAllSearchResultsRouteWithHubShell = (): Route =>
	createRouteWithHubShell(
		createHubRoutePropsWithCustomizeMenu(true),
		AsyncArticlesSearch,
		HUB_ROUTES.ALL_SEARCH_RESULTS.path,
		HUB_ROUTES.ALL_SEARCH_RESULTS.name,
		[],
		'full-width',
		{ hideNavbar: true },
	);
